import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { ContactContext } from 'contexts/contactContext'
import { Button, Popover, Space } from 'antd'
import FormalityMoveBtn from './FormalityMoveBtn'

import type { StepsProps } from 'antd'
import {
  PopoverBody,
  ProgressBar,
  ProgressBarWrapper,
  ProgressTitleWrapper,
  StepDate,
  StepTitle,
  StepTitleWrapper,
} from './styles'

export interface HistoricType {
  final: string
  lastDate: string
  createdAt: string
}

const FormalityProgressStatus = () => {
  const { t } = useTranslation('legalFormalities')
  const { legalFormality } = useContext(CurrentLegalFormalityContext)
  const { contact } = useContext(ContactContext)
  const formalityStatus = legalFormality.status
  const lastHistorics = legalFormality.lastStatusesHistorics || []
  const steps = legalFormality.steps || []
  const displayableSteps = steps.filter((step: string) => {
    return ['legal_authority_refused', 'wrong_rcs_status'].indexOf(step) === -1
  })
  let currentStep =
    formalityStatus === 'legal_authority_refused'
      ? 'legal_authority_dropped'
      : formalityStatus
  currentStep =
    formalityStatus === 'wrong_rcs_status'
      ? 'waiting_form_conformity'
      : currentStep
  const currentSepIndex: number = displayableSteps.indexOf(currentStep)
  const isAborted =
    ['flow_aborted', 'wrong_rcs_status'].indexOf(legalFormality.status) !== -1
  const isFinished = legalFormality.status === 'formality_done'
  console.log(isAborted)

  const lastStepDate = (step: string) => {
    if (step === 'paid') {
      return dayjs(legalFormality.createdAt).format('DD/MM/YYYY')
    }

    const lastHistoric = lastHistorics.find(
      (historic: HistoricType) => historic.final === step
    )
    return lastHistoric
      ? dayjs(lastHistoric.lastDate).format('DD/MM/YYYY')
      : '-'
  }

  const customDot: StepsProps['progressDot'] = (dot, { status, index }) => {
    const state = displayableSteps[index]
    let stepState = t(`show.progress.steps.state.${status}`)
    let statusNameText = t(`status.name.${state}`)
    let reason = null

    if (
      state === 'legal_authority_dropped' &&
      formalityStatus === 'legal_authority_refused'
    ) {
      stepState = t('show.progress.steps.state.error')
      statusNameText = t(`status.name.${state}`)
      reason = legalFormality.authorityRefusalReason
    }

    return (
      <>
        <Popover
          content={
            <PopoverBody>
              <span>{statusNameText}</span>
              <span>
                {t('show.progress.popover.state')}: {stepState}
              </span>
              {reason && (
                <span>
                  {t('show.progress.popover.reason')}
                  {': '}
                  {reason}
                </span>
              )}
            </PopoverBody>
          }
        >
          {dot}
        </Popover>
      </>
    )
  }

  const buildItems = () => {
    return displayableSteps.map((step: string) => {
      let stepStatus = 'wait'
      const stepTitle = t(`show.progress.steps.name.${step}`)
      let stepDate = lastStepDate(step)
      formalityStatus === 'wrong_rcs_status' && (stepStatus = 'error')

      if (
        step === 'legal_authority_dropped' &&
        formalityStatus === 'legal_authority_refused'
      ) {
        stepDate = lastStepDate('legal_authority_refused')
        return {
          status: 'error',
          title: (
            <StepTitleWrapper>
              <StepTitle>{t(`status.name.legal_authority_refused`)}</StepTitle>
              <StepDate>{stepDate}</StepDate>
            </StepTitleWrapper>
          ),
        }
      }

      if (
        step === 'waiting_form_conformity' &&
        formalityStatus === 'wrong_rcs_status'
      ) {
        stepDate = lastStepDate('wrong_rcs_status')
        return {
          status: 'error',
          title: (
            <StepTitleWrapper>
              <StepTitle>{t(`status.name.wrong_rcs_status`)}</StepTitle>
              <StepDate>{stepDate}</StepDate>
            </StepTitleWrapper>
          ),
        }
      }

      const stepIndex = displayableSteps.indexOf(step)

      switch (true) {
        case isAborted:
        case isFinished:
        case stepIndex < currentSepIndex:
          stepStatus = 'finish'
          break
        case stepIndex == currentSepIndex:
          stepStatus = 'process'
          break
        default:
      }

      return {
        status: stepStatus,
        title: (
          <StepTitleWrapper>
            <StepTitle>{stepTitle}</StepTitle>
            <StepDate>{stepDate}</StepDate>
          </StepTitleWrapper>
        ),
      }
    })
  }

  return (
    <ProgressBarWrapper>
      <ProgressTitleWrapper>
        <h4>
          {t('show.progress.title')}
          {':'}
          {` [${legalFormality.id}] `}
          {`[${legalFormality.uuid}] `}
          {t(
            `products:identificationToken.${legalFormality.purchase.identificationToken}`,
            ''
          )}
          {' - '}
          {`${contact?.society?.name}`}
          {` (${contact?.contactDetail?.firstname} ${contact?.contactDetail?.lastname})`}
          {' - '}
        </h4>
        <Space>
          <FormalityMoveBtn legalFormalityId={legalFormality.id} />
          <Button href={'/admin2/v2/formalities'}>Formalités</Button>
        </Space>
      </ProgressTitleWrapper>
      <ProgressBar
        className={`
          formality-progress 
          ${isAborted && 'aborted'} 
          ${isFinished && 'finished'}
        `}
        progressDot={customDot}
        current={currentSepIndex - 1}
        items={buildItems()}
      />
    </ProgressBarWrapper>
  )
}

export default FormalityProgressStatus
